// =====================================================================
// paginas/contratos.js — O fluxo do contrato, do candidato ao PDF enviado.
//
// A aba abre numa TELA DE TRABALHO, não numa tabela: contrato não é um
// registro que se consulta, é um processo que se conduz. Dois caminhos:
//
//   Criar novo contrato → assistente: candidato → conferência → condições →
//                         exceções → ficha (revisar, gerar, enviar)
//   Pesquisar contrato  → digita o nome, acha, abre a ficha
//
// A CONFERÊNCIA (passo 2) é o coração disto. Os dados vêm do cadastro que o
// próprio inquilino preencheu no portal — e ninguém assina um contrato com
// dado que o cliente digitou sem alguém ter olhado. Por isso cada campo tem
// "Confirmar" e "Editar", e o assistente não avança enquanto houver campo
// obrigatório sem confirmação. O sistema não decide por você: ele obriga
// você a decidir.
//
// Sobre a IA: ela revisa e PROPÕE cláusula para as exceções. O PDF sai por
// substituição determinística — mesmo dado, mesmo papel, sempre. Se o texto
// final viesse de um modelo de linguagem, dois contratos iguais poderiam
// sair diferentes, e nenhum seria reproduzível numa disputa.
// =====================================================================

import { sb, api } from '../../../../packages/ui/cliente.js';
import { icon, escapeHtml, modal, badge, checar } from '../ui.js';
import { moeda, data as fmtData, cpf as fmtCpf, telefone as fmtTel } from '../../../../packages/ui/format.js';

const STATUS = {
  rascunho:              ['Rascunho', 'cinza'],
  aguardando_assinatura: ['Aguardando assinatura', 'ambar'],
  ativo:                 ['Ativo', 'verde'],
  encerrado:             ['Encerrado', 'cinza'],
  rescindido:            ['Rescindido', 'vermelho']
};

let raiz;

export async function pagContratos(el) {
  raiz = el;
  telaInicial();
}

// ─── Tela inicial: os dois caminhos ──────────────────────────────────
async function telaInicial() {
  raiz.innerHTML = `
    <div class="cabecalho">
      <h2>Contratos</h2>
      <button id="b-perguntas" title="As perguntas que o assistente faz ao montar um contrato">
        ${icon('config')} Cadastrar e editar perguntas
      </button>
    </div>

    <div class="grade k2" style="margin-bottom:20px">
      <button class="cartao acao-grande" id="b-novo">
        ${icon('novo')}
        <div><b>Criar novo contrato</b>
             <span>A partir do cadastro que o inquilino fez no portal.</span></div>
      </button>
      <button class="cartao acao-grande" id="b-buscar">
        ${icon('busca')}
        <div><b>Pesquisar contrato</b>
             <span>Pelo nome do inquilino ou pelo número do contrato.</span></div>
      </button>
    </div>

    <div class="cartao">
      <h3>${icon('contrato')} Últimos contratos</h3>
      <div id="recentes" class="carregando">Carregando…</div>
    </div>`;

  raiz.querySelector('#b-novo').onclick      = () => passo1(novoEstado());
  raiz.querySelector('#b-buscar').onclick    = telaBusca;
  raiz.querySelector('#b-perguntas').onclick = telaPerguntas;

  const { data } = await sb.from('contratos')
    .select('id,numero,status,valor_aluguel,inquilinos(nome),imoveis(codigo)')
    .order('criado_em', { ascending: false }).limit(8);

  const box = raiz.querySelector('#recentes');
  if (!data || !data.length) {
    box.className = 'vazio';
    box.textContent = 'Nenhum contrato ainda.';
    return;
  }
  box.className = '';
  box.innerHTML = `<div class="lista-sel">${data.map(linhaContrato).join('')}</div>`;
  ligarLista(box);
}

const linhaContrato = (c) => {
  const [txt, cor] = STATUS[c.status] || [c.status, 'cinza'];
  return `<button class="item-sel" data-id="${c.id}">
    <div class="quem">
      <b>${escapeHtml((c.inquilinos && c.inquilinos.nome) || 'Sem inquilino')}</b>
      <span>${escapeHtml((c.imoveis && c.imoveis.codigo) || '—')} · nº ${escapeHtml(c.numero || '—')}
            · ${c.valor_aluguel ? moeda(c.valor_aluguel) : '—'}</span>
    </div>
    ${badge(txt, cor)}
  </button>`;
};

const ligarLista = (box) =>
  box.querySelectorAll('[data-id]').forEach(b => b.onclick = () => fichaContrato(b.dataset.id));

// ─── Pesquisar ───────────────────────────────────────────────────────
function telaBusca() {
  raiz.innerHTML = `
    <div class="cabecalho">
      <button class="icone" id="voltar" title="Voltar">${icon('voltar')}</button>
      <h2>Pesquisar contrato</h2>
    </div>
    <div class="cartao">
      <label>Nome do inquilino ou número do contrato
        <input id="q" placeholder="Comece a digitar…" autocomplete="off">
      </label>
      <div id="res" style="margin-top:14px"></div>
    </div>`;

  raiz.querySelector('#voltar').onclick = telaInicial;
  const q = raiz.querySelector('#q');
  const res = raiz.querySelector('#res');
  q.focus();

  // Espera a pessoa parar de digitar. Sem isso, cada tecla vira uma consulta,
  // e a lista pisca o resultado de uma busca que já não é a atual.
  let t;
  q.oninput = () => {
    clearTimeout(t);
    const termo = q.value.trim();
    if (termo.length < 2) { res.innerHTML = ''; return; }
    t = setTimeout(() => buscar(termo, res), 250);
  };
}

async function buscar(termo, res) {
  res.innerHTML = '<div class="carregando">Procurando…</div>';
  const like = `*${termo}*`;

  const [porNome, porNumero] = await Promise.all([
    sb.from('contratos')
      .select('id,numero,status,valor_aluguel,inquilinos!inner(nome),imoveis(codigo)')
      .ilike('inquilinos.nome', like).limit(12),
    sb.from('contratos')
      .select('id,numero,status,valor_aluguel,inquilinos(nome),imoveis(codigo)')
      .ilike('numero', like).limit(12)
  ]);

  const mapa = new Map();
  [...(porNome.data || []), ...(porNumero.data || [])].forEach(c => mapa.set(c.id, c));
  const achados = [...mapa.values()];

  if (!achados.length) {
    res.innerHTML = `<p class="vazio">Nenhum contrato com “${escapeHtml(termo)}”.</p>`;
    return;
  }
  res.innerHTML = `<div class="lista-sel">${achados.map(linhaContrato).join('')}</div>`;
  ligarLista(res);
}

// ─── Ficha do contrato ───────────────────────────────────────────────
async function fichaContrato(id) {
  raiz.innerHTML = '<div class="carregando">Abrindo o contrato…</div>';

  const { data: c } = await sb.from('contratos')
    .select('*,inquilinos(*),imoveis(*),fiadores(*)')
    .eq('id', id).maybeSingle();
  if (!c) { raiz.innerHTML = '<p class="vazio">Contrato não encontrado.</p>'; return; }

  const { data: docs } = await sb.from('contrato_documentos')
    .select('id,pdf_path,sha256,gerado_em,assinado,modelo_versao')
    .eq('contrato_id', id).order('gerado_em', { ascending: false });

  const [txt, cor] = STATUS[c.status] || [c.status, 'cinza'];
  const extras = c.clausulas_extras || [];

  raiz.innerHTML = `
    <div class="cabecalho">
      <button class="icone" id="voltar" title="Voltar">${icon('voltar')}</button>
      <h2>Contrato nº ${escapeHtml(c.numero || '—')}</h2>
      ${badge(txt, cor)}
    </div>

    <div class="grade k2">
      <div class="cartao">
        <h3>${icon('pessoa')} Partes</h3>
        <p><b>${escapeHtml((c.inquilinos && c.inquilinos.nome) || '—')}</b><br>
           <span class="dica">${fmtCpf(c.inquilinos && c.inquilinos.cpf) || 'sem CPF'}
           · ${fmtTel(c.inquilinos && c.inquilinos.telefone) || 'sem telefone'}</span></p>
        <p><b>Imóvel:</b> ${escapeHtml((c.imoveis && c.imoveis.codigo) || '—')}
           — ${escapeHtml((c.imoveis && c.imoveis.logradouro) || '')}</p>
        <p><b>Garantia:</b> ${escapeHtml(c.garantia_tipo)}${
          (c.fiadores || []).length ? ' — ' + c.fiadores.map(f => escapeHtml(f.nome)).join(', ') : ''}</p>
      </div>

      <div class="cartao">
        <h3>${icon('dinheiro')} Condições</h3>
        <p><b>${moeda(c.valor_aluguel)}</b> · vence dia ${c.dia_vencimento}</p>
        <p>${fmtData(c.data_inicio)} → ${fmtData(c.data_fim)} (${c.prazo_meses || '?'} meses)</p>
        <p>Reajuste ${escapeHtml(c.indice_reajuste)} a cada ${c.meses_reajuste} meses</p>
      </div>
    </div>

    ${extras.length ? `
    <div class="cartao" style="margin-top:16px">
      <h3>${icon('contrato')} Cláusulas fora do modelo (${extras.length})</h3>
      ${extras.map(e => `
        <p><b>${escapeHtml(e.titulo)}</b>
          ${badge(e.origem === 'ia' ? 'sugerida por IA, aprovada' : 'da pergunta',
                  e.origem === 'ia' ? 'azul' : 'cinza')}
          <br><span class="dica">${escapeHtml(e.texto)}</span></p>`).join('')}
    </div>` : ''}

    <div class="cartao" style="margin-top:16px">
      <h3>${icon('config')} Ações</h3>
      <div style="display:flex;gap:8px;flex-wrap:wrap">
        <button id="b-revisar">${icon('ia')} Revisar com IA</button>
        <button class="primaria" id="b-gerar">${icon('contrato')} Gerar contrato</button>
        <button id="b-enviar" ${c.documento_url ? '' : 'disabled'}>${icon('whatsapp')} Enviar por WhatsApp</button>
      </div>
      <div id="saida" style="margin-top:14px"></div>
    </div>

    <div class="cartao" style="margin-top:16px">
      <h3>${icon('documento')} Versões geradas</h3>
      ${(docs || []).length ? `<div class="lista-sel">${docs.map(d => `
        <button class="item-sel" data-doc="${escapeHtml(d.pdf_path)}">
          <div class="quem">
            <b>Gerado em ${fmtData(d.gerado_em)}</b>
            <span>modelo v${d.modelo_versao || '?'} · sha256 ${escapeHtml((d.sha256 || '').slice(0, 12))}…
                  ${d.assinado ? '· assinado' : ''}</span>
          </div>
          ${icon('download')}
        </button>`).join('')}</div>`
        : '<p class="vazio">Nenhum PDF gerado ainda.</p>'}
    </div>`;

  raiz.querySelector('#voltar').onclick    = telaInicial;
  raiz.querySelector('#b-revisar').onclick = () => revisar(id);
  raiz.querySelector('#b-gerar').onclick   = () => gerar(id);

  const bEnv = raiz.querySelector('#b-enviar');
  if (bEnv && !bEnv.disabled) bEnv.onclick = () => enviar(c);

  raiz.querySelectorAll('[data-doc]').forEach(b => b.onclick = () => baixar(b.dataset.doc));
}

const saida = (html, cls = 'info') => {
  const box = raiz.querySelector('#saida');
  if (box) box.innerHTML = `<div class="aviso ${cls}">${html}</div>`;
};

// ─── Revisar (IA propõe) ─────────────────────────────────────────────
async function revisar(id) {
  saida('Revisando…');
  try {
    const r = await api('revisar-contrato', { contrato_id: id });

    if (!r.achados.length) {
      saida(`${icon('ok')} Nenhum problema encontrado. Pode gerar.`, 'ok');
      return;
    }

    const listaHtml = r.achados.map((a, i) => `
      <div class="achado g-${escapeHtml(a.gravidade)}">
        <div class="corpo-achado">
          ${badge(a.gravidade, a.gravidade === 'alta' ? 'vermelho'
                            : a.gravidade === 'media' ? 'ambar' : 'azul')}
          <b>${escapeHtml(a.campo || '')}</b>
          <p>${escapeHtml(a.mensagem)}</p>
          ${a.clausula ? `<div class="clausula-sug">
              <b>${escapeHtml(a.clausula.titulo)}</b>
              <p>${escapeHtml(a.clausula.texto)}</p></div>` : ''}
          ${a.decisao && a.decisao !== 'pendente' ? badge(a.decisao, 'cinza') : ''}
        </div>
        ${a.tipo === 'sugestao_clausula' && a.decisao === 'pendente' ? `
          <div class="acoes-achado">
            <button class="primaria" data-aceitar="${i}">Aceitar</button>
            <button data-editar="${i}">Editar e aceitar</button>
            <button data-rejeitar="${i}">Rejeitar</button>
          </div>` : ''}
      </div>`).join('');

    saida(`
      <b>${r.resumo.total} ponto(s):</b> ${r.resumo.alta} alto(s),
      ${r.resumo.media} médio(s), ${r.resumo.baixa} baixo(s).
      <p class="dica">Nada disso entrou no contrato. Sugestão de cláusula só vale depois que uma pessoa aceita.</p>
      <div class="achados">${listaHtml}</div>`);

    const decidir = async (i, decisao, texto, titulo) => {
      await api('aprovar-clausula', {
        contrato_id: id, revisao_id: r.revisao_id, indice: Number(i), decisao, texto, titulo
      });
      fichaContrato(id);
    };

    raiz.querySelectorAll('[data-aceitar]').forEach(b =>
      b.onclick = () => decidir(b.dataset.aceitar, 'aceita'));
    raiz.querySelectorAll('[data-rejeitar]').forEach(b =>
      b.onclick = () => decidir(b.dataset.rejeitar, 'rejeitada'));
    raiz.querySelectorAll('[data-editar]').forEach(b => b.onclick = () => {
      const a = r.achados[Number(b.dataset.editar)];
      modal({
        titulo: 'Editar a cláusula antes de aceitar',
        aviso: 'O que vale é o texto que <b>você</b> aprova — não o que a IA escreveu.',
        dados: { titulo: a.clausula.titulo, texto: a.clausula.texto },
        campos: [
          { nome: 'titulo', label: 'Título da cláusula', req: true, full: true },
          { nome: 'texto',  label: 'Texto', tipo: 'textarea', req: true, full: true }
        ],
        salvar: 'Aceitar cláusula',
        onSalvar: (d) => decidir(b.dataset.editar, 'aceita', d.texto, d.titulo)
      });
    });

  } catch (e) {
    saida(escapeHtml(e.message), 'erro');
  }
}

// ─── Gerar, baixar, enviar ───────────────────────────────────────────
async function gerar(id) {
  saida('Gerando o PDF…');
  try {
    const r = await api('gerar-contrato', { contrato_id: id });
    saida(`${icon('ok')} Contrato gerado — versão ${r.versao}, ${r.clausulas} cláusulas.
           <a href="${r.url}" target="_blank" rel="noopener">Abrir o PDF</a>`, 'ok');
    setTimeout(() => fichaContrato(id), 1500);
  } catch (e) {
    // A recusa por falta de dado vem com a lista do que falta. Mostrar essa
    // lista é o ponto todo de recusar: "não deu" não ajuda ninguém.
    const falta = e.falta || (e.corpo && e.corpo.falta);
    saida(falta
      ? `<b>Faltam dados que o contrato exige:</b>
         <ul>${falta.map(f => `<li>${escapeHtml(f)}</li>`).join('')}</ul>`
      : escapeHtml(e.message), 'erro');
  }
}

async function baixar(caminho) {
  const { data, error } = await sb.storage.from('contratos').createSignedUrl(caminho, 300);
  if (error) return saida(escapeHtml(error.message), 'erro');
  window.open(data.signedUrl, '_blank', 'noopener');
}

async function enviar(c) {
  const { data: equipe } = await sb.from('perfis')
    .select('id,nome,telefone').eq('aprovado', true).order('nome');

  const opcoes = [
    { valor: 'inquilino:', texto: `Inquilino — ${(c.inquilinos && c.inquilinos.nome) || ''}` },
    ...(c.fiadores || []).map(f => ({ valor: `fiador:${f.id}`, texto: `Fiador — ${f.nome}` })),
    ...(equipe || []).filter(p => p.telefone)
      .map(p => ({ valor: `equipe:${p.id}`, texto: `Equipe — ${p.nome}` }))
  ];

  modal({
    titulo: 'Enviar o contrato por WhatsApp',
    aviso: 'O número vem do <b>cadastro</b>, nunca digitado na hora: o PDF tem CPF, RG, renda e endereço. '
         + 'Se o telefone estiver errado, corrija no cadastro — lá o conserto vale para sempre.',
    campos: [
      { nome: 'para', label: 'Para quem', tipo: 'select', opcoes, full: true },
      { nome: 'mensagem', label: 'Mensagem (opcional)', tipo: 'textarea', full: true,
        dica: 'Em branco, vai o texto padrão com a instrução de assinatura.' }
    ],
    salvar: 'Enviar',
    onSalvar: async (d) => {
      const [quem, ident] = String(d.para).split(':');
      const r = await api('enviar-contrato', {
        contrato_id: c.id,
        para: quem,
        fiador_id: quem === 'fiador' ? ident : undefined,
        perfil_id: quem === 'equipe' ? ident : undefined,
        mensagem: d.mensagem || undefined
      });
      saida(r.ja_enviado
        ? `${icon('alerta')} ${escapeHtml(r.mensagem)}`
        : `${icon('ok')} Enviado para ${escapeHtml(r.enviado_para.nome)}. O link expira em 7 dias.`,
        r.ja_enviado ? 'info' : 'ok');
    }
  });
}

// ─── Perguntas do contrato ───────────────────────────────────────────
async function telaPerguntas() {
  raiz.innerHTML = '<div class="carregando">Carregando as perguntas…</div>';
  const { data: perguntas } = await sb.from('contrato_perguntas').select('*').order('ordem');

  raiz.innerHTML = `
    <div class="cabecalho">
      <button class="icone" id="voltar" title="Voltar">${icon('voltar')}</button>
      <h2>Perguntas do contrato</h2>
      <button class="primaria" id="b-nova">${icon('novo')} Nova pergunta</button>
    </div>

    <div class="aviso info" style="margin-bottom:16px">
      ${icon('info')} São as exceções que o assistente pergunta ao montar cada contrato.
      A resposta pode preencher uma variável do modelo, virar uma cláusula pronta, ou ser
      uma anotação em português comum — e aí a IA <b>propõe</b> a redação jurídica, que você aprova.
    </div>

    <div class="cartao">
      ${(perguntas || []).length ? `<div class="lista-sel">${perguntas.map(p => `
        <button class="item-sel" data-p="${p.id}">
          <div class="quem">
            <b>${escapeHtml(p.rotulo)}</b>
            <span>${escapeHtml(p.tipo)} · ${escapeHtml(p.alvo)}${p.obrigatoria ? ' · obrigatória' : ''}${p.ativa ? '' : ' · inativa'}</span>
          </div>
          ${icon('editar')}
        </button>`).join('')}</div>`
        : '<p class="vazio">Nenhuma pergunta cadastrada.</p>'}
    </div>`;

  raiz.querySelector('#voltar').onclick = telaInicial;
  raiz.querySelector('#b-nova').onclick = () => formPergunta(null);
  raiz.querySelectorAll('[data-p]').forEach(b =>
    b.onclick = () => formPergunta((perguntas || []).find(p => p.id === b.dataset.p)));
}

function formPergunta(p) {
  modal({
    titulo: p ? 'Editar pergunta' : 'Nova pergunta',
    dados: p || { tipo: 'texto', alvo: 'variavel', ordem: 100, ativa: true },
    campos: [
      { nome: 'rotulo', label: 'Pergunta', req: true, full: true,
        dica: 'Como aparece para quem monta o contrato. Ex.: "Número da vaga de garagem"' },
      { nome: 'chave', label: 'Chave', req: true,
        dica: 'Sem espaço nem acento. Vira {{chave}} no modelo. Ex.: vaga_garagem' },
      { nome: 'tipo', label: 'Tipo de resposta', tipo: 'select',
        opcoes: [
          { valor: 'texto', texto: 'Texto curto' },
          { valor: 'texto_longo', texto: 'Texto longo' },
          { valor: 'numero', texto: 'Número' },
          { valor: 'moeda', texto: 'Valor em reais' },
          { valor: 'data', texto: 'Data' },
          { valor: 'sim_nao', texto: 'Sim ou não' },
          { valor: 'selecao', texto: 'Escolha de uma lista' }
        ] },
      { nome: 'alvo', label: 'O que a resposta faz', tipo: 'select', full: true,
        opcoes: [
          { valor: 'variavel', texto: 'Preenche uma {{variável}} do modelo' },
          { valor: 'clausula', texto: 'Vira uma cláusula extra no contrato' },
          { valor: 'anotacao', texto: 'Anotação — a IA propõe a cláusula, você aprova' }
        ],
        dica: 'Anotação é para o que muda a cada contrato: escreva em português comum.' },
      { nome: 'clausula_titulo', label: 'Título da cláusula (se virar cláusula)', full: true },
      { nome: 'clausula_texto', label: 'Texto da cláusula', tipo: 'textarea', full: true,
        dica: 'Use {{resposta}} onde entra o que foi respondido. Ex.: "…a vaga nº {{resposta}}…"' },
      { nome: 'ajuda', label: 'Dica para quem responde', full: true },
      { nome: 'obrigatoria', label: 'Obrigatória', tipo: 'checkbox' },
      { nome: 'ativa', label: 'Ativa', tipo: 'checkbox' },
      { nome: 'ordem', label: 'Ordem', tipo: 'number' }
    ],
    onSalvar: async (d) => {
      d.ordem = Number(d.ordem) || 100;
      if (p) checar(await sb.from('contrato_perguntas').update(d).eq('id', p.id));
      else   checar(await sb.from('contrato_perguntas').insert(d));
      telaPerguntas();
    }
  });
}

// ─── Assistente de novo contrato ─────────────────────────────────────
//
// Um objeto só, passado de passo em passo. Nenhum passo lê o DOM do
// anterior — e sair da aba e voltar sempre recomeça limpo, porque
// assistente pela metade que "lembra" de outro contrato é como se gera
// contrato para a pessoa errada.
const novoEstado = () => ({ inquilino: null, confirmados: new Set(), condicoes: {}, respostas: {} });

const PASSOS = ['Candidato', 'Conferência', 'Condições', 'Exceções'];
const trilha = (n) => `
  <ol class="trilha">
    ${PASSOS.map((p, i) => `
      <li class="${i + 1 < n ? 'feito' : i + 1 === n ? 'atual' : ''}">
        <span>${i + 1}</span>${p}
      </li>`).join('')}
  </ol>`;

// Passo 1 — quem vai alugar
function passo1(st) {
  raiz.innerHTML = `
    <div class="cabecalho">
      <button class="icone" id="voltar" title="Voltar">${icon('voltar')}</button>
      <h2>Novo contrato</h2>
    </div>
    ${trilha(1)}
    <div class="cartao">
      <h3>${icon('pessoa')} Quem vai alugar</h3>
      <label>Busque o cadastro que o candidato preencheu no portal
        <input id="q" placeholder="Nome ou CPF…" autocomplete="off">
      </label>
      <div id="res" style="margin-top:14px"></div>
    </div>`;

  raiz.querySelector('#voltar').onclick = telaInicial;
  const q = raiz.querySelector('#q');
  const res = raiz.querySelector('#res');
  q.focus();

  let t;
  q.oninput = () => {
    clearTimeout(t);
    const termo = q.value.trim();
    if (termo.length < 2) { res.innerHTML = ''; return; }
    t = setTimeout(async () => {
      const { data } = await sb.from('inquilinos')
        .select('id,nome,cpf,telefone,status')
        .or(`nome.ilike.*${termo}*,cpf.ilike.*${termo}*`)
        .order('criado_em', { ascending: false }).limit(10);

      if (!data || !data.length) {
        res.innerHTML = `<p class="vazio">Ninguém com “${escapeHtml(termo)}”.
          O candidato precisa se cadastrar no portal antes.</p>`;
        return;
      }
      res.innerHTML = `<div class="lista-sel">${data.map(i => `
        <button class="item-sel" data-i="${i.id}">
          <div class="quem">
            <b>${escapeHtml(i.nome)}</b>
            <span>${fmtCpf(i.cpf) || 'sem CPF'} · ${fmtTel(i.telefone) || 'sem telefone'}</span>
          </div>
          ${badge(i.status, i.status === 'aprovado' ? 'verde'
                          : i.status === 'reprovado' ? 'vermelho' : 'ambar')}
        </button>`).join('')}</div>`;

      res.querySelectorAll('[data-i]').forEach(b => b.onclick = async () => {
        const { data: inq } = await sb.from('inquilinos').select('*').eq('id', b.dataset.i).maybeSingle();
        st.inquilino = inq;
        passo2(st);
      });
    }, 250);
  };
}

// Passo 2 — conferência campo a campo
const CAMPOS_CONFERE = [
  { k: 'nome', r: 'Nome completo', req: true },
  { k: 'cpf', r: 'CPF', req: true, fmt: fmtCpf },
  { k: 'rg', r: 'Identidade', req: true },
  { k: 'orgao_emissor', r: 'Órgão emissor' },
  { k: 'data_nascimento', r: 'Nascimento', tipo: 'date', fmt: fmtData },
  { k: 'estado_civil', r: 'Estado civil', req: true },
  { k: 'profissao', r: 'Profissão', req: true },
  { k: 'nacionalidade', r: 'Nacionalidade' },
  { k: 'renda_mensal', r: 'Renda mensal', tipo: 'number', fmt: moeda },
  { k: 'telefone', r: 'Telefone', req: true, fmt: fmtTel },
  { k: 'email', r: 'E-mail' },
  { k: 'endereco_atual', r: 'Endereço atual', req: true },
  { k: 'conjuge_nome', r: 'Cônjuge' },
  { k: 'conjuge_cpf', r: 'CPF do cônjuge' }
];

function passo2(st) {
  const i = st.inquilino;

  const linha = (c) => {
    const v = i[c.k];
    const cheio = v != null && String(v).trim() !== '';
    const ok = st.confirmados.has(c.k);
    return `<tr class="${ok ? 'confirmado' : ''}">
      <td>${escapeHtml(c.r)}${c.req ? ' <span class="req">*</span>' : ''}</td>
      <td class="valor">${cheio
        ? escapeHtml(c.fmt ? String(c.fmt(v)) : String(v))
        : '<i class="vazio-td">em branco</i>'}</td>
      <td class="acoes">
        <button data-conf="${c.k}" class="${ok ? 'ok' : ''}" ${cheio ? '' : 'disabled'}>
          ${icon(ok ? 'ok' : 'check')} ${ok ? 'Confirmado' : 'Confirmar'}
        </button>
        <button data-ed="${c.k}">${icon('editar')} Editar</button>
      </td>
    </tr>`;
  };

  const faltam = CAMPOS_CONFERE.filter(c => c.req && !st.confirmados.has(c.k));

  raiz.innerHTML = `
    <div class="cabecalho">
      <button class="icone" id="voltar" title="Voltar">${icon('voltar')}</button>
      <h2>Conferir os dados de ${escapeHtml(i.nome)}</h2>
    </div>
    ${trilha(2)}
    <div class="aviso ${faltam.length ? 'info' : 'ok'}" style="margin-bottom:14px">
      ${icon(faltam.length ? 'info' : 'ok')}
      ${faltam.length
        ? `Estes dados foram digitados pelo próprio candidato no portal. Confirme um a um —
           faltam <b>${faltam.length}</b> obrigatório(s).`
        : 'Todos os dados obrigatórios foram conferidos.'}
    </div>
    <div class="cartao">
      <table class="conferencia"><tbody>${CAMPOS_CONFERE.map(linha).join('')}</tbody></table>
    </div>
    <div class="rodape-passo">
      <button id="todos">Confirmar todos os preenchidos</button>
      <button class="primaria" id="seguir" ${faltam.length ? 'disabled' : ''}>Continuar</button>
    </div>`;

  raiz.querySelector('#voltar').onclick = () => passo1(st);

  raiz.querySelectorAll('[data-conf]').forEach(b => b.onclick = () => {
    const k = b.dataset.conf;
    if (st.confirmados.has(k)) st.confirmados.delete(k); else st.confirmados.add(k);
    passo2(st);
  });

  raiz.querySelectorAll('[data-ed]').forEach(b => b.onclick = () => {
    const c = CAMPOS_CONFERE.find(x => x.k === b.dataset.ed);
    modal({
      titulo: `Editar: ${c.r}`,
      dados: { valor: i[c.k] ?? '' },
      campos: [{ nome: 'valor', label: c.r, tipo: c.tipo || 'text', full: true }],
      onSalvar: async (d) => {
        checar(await sb.from('inquilinos').update({ [c.k]: d.valor }).eq('id', i.id));
        i[c.k] = d.valor;
        st.confirmados.add(c.k);      // quem editou, conferiu
        passo2(st);
      }
    });
  });

  raiz.querySelector('#todos').onclick = () => {
    CAMPOS_CONFERE.forEach(c => {
      const v = i[c.k];
      if (v != null && String(v).trim() !== '') st.confirmados.add(c.k);
    });
    passo2(st);
  };

  const seguir = raiz.querySelector('#seguir');
  if (!seguir.disabled) seguir.onclick = () => passo3(st);
}

// Passo 3 — imóvel e condições
async function passo3(st) {
  raiz.innerHTML = '<div class="carregando">Carregando imóveis…</div>';

  const [imoveis, contas, tenant] = await Promise.all([
    sb.from('imoveis').select('id,codigo,logradouro,valor_aluguel_ref,status')
      .in('status', ['disponivel', 'manutencao']).order('codigo'),
    sb.from('contas_financeiras').select('id,nome').eq('ativa', true).order('nome'),
    sb.from('tenants').select('dia_vencimento_padrao,indice_reajuste_padrao,multa_atraso_pct,juros_mora_pct_mes,taxa_adm_pct')
      .limit(1).maybeSingle()
  ]);

  const t = tenant.data || {};
  const d = st.condicoes;
  const hoje = new Date().toISOString().slice(0, 10);

  raiz.innerHTML = `
    <div class="cabecalho">
      <button class="icone" id="voltar" title="Voltar">${icon('voltar')}</button>
      <h2>Imóvel e condições</h2>
    </div>
    ${trilha(3)}
    <div class="cartao">
      <form id="f" class="linha-form">
        <label style="grid-column:1/-1">Imóvel
          <select name="imovel_id" required>
            <option value="">— escolha —</option>
            ${(imoveis.data || []).map(im => `
              <option value="${im.id}" data-aluguel="${im.valor_aluguel_ref || ''}"
                ${d.imovel_id === im.id ? 'selected' : ''}>
                ${escapeHtml(im.codigo)} — ${escapeHtml(im.logradouro || '')}${im.status === 'manutencao' ? ' (em manutenção)' : ''}
              </option>`).join('')}
          </select>
          <small class="dica">Só aparecem imóveis livres — "alugado" é consequência de contrato ativo, não uma escolha.</small>
        </label>

        <label>Número do contrato<input name="numero" value="${escapeHtml(d.numero || '')}" required></label>
        <label>Tipo
          <select name="tipo">
            <option value="residencial">Residencial</option>
            <option value="comercial" ${d.tipo === 'comercial' ? 'selected' : ''}>Comercial</option>
          </select>
        </label>

        <label>Início<input type="date" name="data_inicio" value="${d.data_inicio || hoje}" required></label>
        <label>Prazo (meses)<input type="number" name="prazo" min="1" value="${d.prazo || 30}" required>
          <small class="dica">A desocupação sem motivo ao fim do prazo exige 30 meses ou mais (Lei 8.245/91, arts. 46/47).</small>
        </label>

        <label>Valor do aluguel<input type="number" step="0.01" name="valor_aluguel"
               value="${d.valor_aluguel || ''}" required></label>
        <label>Dia do vencimento<input type="number" name="dia_vencimento" min="1" max="28"
               value="${d.dia_vencimento || t.dia_vencimento_padrao || 5}" required></label>

        <label>Condomínio<input type="number" step="0.01" name="valor_condominio" value="${d.valor_condominio || 0}"></label>
        <label>IPTU<input type="number" step="0.01" name="valor_iptu" value="${d.valor_iptu || 0}"></label>

        <label>Garantia
          <select name="garantia_tipo">
            <option value="fiador">Fiador</option>
            <option value="caucao">Caução</option>
            <option value="seguro_fianca">Seguro-fiança</option>
            <option value="titulo_capitalizacao">Título de capitalização</option>
            <option value="nenhuma">Nenhuma</option>
          </select>
        </label>
        <label>Valor da garantia<input type="number" step="0.01" name="garantia_valor" value="${d.garantia_valor || ''}"></label>

        <label>Índice de reajuste
          <select name="indice_reajuste">
            ${['IGPM', 'IPCA', 'INCC', 'IVAR', 'NENHUM'].map(x =>
              `<option ${x === (d.indice_reajuste || t.indice_reajuste_padrao || 'IGPM') ? 'selected' : ''}>${x}</option>`).join('')}
          </select>
        </label>
        <label>Conta que recebe
          <select name="conta_recebimento_id">
            <option value="">— a padrão do cliente —</option>
            ${(contas.data || []).map(c => `<option value="${c.id}">${escapeHtml(c.nome)}</option>`).join('')}
          </select>
        </label>

        <label>Multa por atraso (%)<input type="number" step="0.01" name="multa_atraso_pct"
               value="${d.multa_atraso_pct ?? t.multa_atraso_pct ?? 2}"></label>
        <label>Juros ao mês (%)<input type="number" step="0.01" name="juros_mora_pct_mes"
               value="${d.juros_mora_pct_mes ?? t.juros_mora_pct_mes ?? 1}"></label>

        <label>Carência da multa rescisória (meses)<input type="number" name="carencia_multa_meses"
               value="${d.carencia_multa_meses ?? 12}"></label>
        <label>Taxa de saída (% do salário mínimo)<input type="number" step="0.01" name="taxa_saida_pct_sm"
               value="${d.taxa_saida_pct_sm ?? 0}"></label>
      </form>
    </div>
    <div class="rodape-passo">
      <button class="primaria" id="seguir">Continuar</button>
    </div>`;

  raiz.querySelector('#voltar').onclick = () => passo2(st);

  const form = raiz.querySelector('#f');
  // O aluguel de referência do imóvel entra sozinho — e continua editável.
  form.imovel_id.onchange = (e) => {
    const opt = e.target.selectedOptions[0];
    const ref = opt && opt.dataset.aluguel;
    if (ref && !form.valor_aluguel.value) form.valor_aluguel.value = ref;
  };

  raiz.querySelector('#seguir').onclick = () => {
    if (!form.reportValidity()) return;
    const fd = Object.fromEntries(new FormData(form));

    const inicio = new Date(fd.data_inicio + 'T12:00:00');
    const fim = new Date(inicio);
    fim.setMonth(fim.getMonth() + Number(fd.prazo));

    st.condicoes = {
      ...fd,
      prazo: Number(fd.prazo),
      data_fim: fim.toISOString().slice(0, 10),
      valor_aluguel: Number(fd.valor_aluguel),
      valor_condominio: Number(fd.valor_condominio || 0),
      valor_iptu: Number(fd.valor_iptu || 0),
      dia_vencimento: Number(fd.dia_vencimento),
      garantia_valor: fd.garantia_valor ? Number(fd.garantia_valor) : null,
      carencia_multa_meses: Number(fd.carencia_multa_meses || 0),
      taxa_saida_pct_sm: Number(fd.taxa_saida_pct_sm || 0),
      multa_atraso_pct: Number(fd.multa_atraso_pct),
      juros_mora_pct_mes: Number(fd.juros_mora_pct_mes),
      conta_recebimento_id: fd.conta_recebimento_id || null
    };
    passo4(st);
  };
}

// Passo 4 — as exceções, e a criação do contrato
async function passo4(st) {
  const { data: perguntas } = await sb.from('contrato_perguntas')
    .select('*').eq('ativa', true).order('ordem');

  const campo = (p) => {
    const v = st.respostas[p.chave] ?? p.padrao ?? '';
    let ctrl;
    if (p.tipo === 'sim_nao') {
      ctrl = `<select name="${p.chave}">
        <option value="nao" ${v === 'nao' ? 'selected' : ''}>Não</option>
        <option value="sim" ${v === 'sim' ? 'selected' : ''}>Sim</option></select>`;
    } else if (p.tipo === 'selecao') {
      ctrl = `<select name="${p.chave}"><option value="">—</option>
        ${(p.opcoes || []).map(o => `<option ${o === v ? 'selected' : ''}>${escapeHtml(o)}</option>`).join('')}</select>`;
    } else if (p.tipo === 'texto_longo') {
      ctrl = `<textarea name="${p.chave}" rows="3" ${p.obrigatoria ? 'required' : ''}>${escapeHtml(v)}</textarea>`;
    } else {
      const tipo = (p.tipo === 'numero' || p.tipo === 'moeda') ? 'number'
                 : p.tipo === 'data' ? 'date' : 'text';
      ctrl = `<input type="${tipo}" name="${p.chave}" value="${escapeHtml(v)}" ${p.obrigatoria ? 'required' : ''}>`;
    }
    return `<label style="grid-column:1/-1">
      ${escapeHtml(p.rotulo)}${p.obrigatoria ? ' <span class="req">*</span>' : ''}
      ${ctrl}
      ${p.ajuda || p.alvo === 'anotacao' ? `<small class="dica">${escapeHtml(p.ajuda || '')}
        ${p.alvo === 'anotacao' ? ' <b>A IA propõe a redação jurídica; você aprova antes de entrar no papel.</b>' : ''}</small>` : ''}
    </label>`;
  };

  raiz.innerHTML = `
    <div class="cabecalho">
      <button class="icone" id="voltar" title="Voltar">${icon('voltar')}</button>
      <h2>Exceções deste contrato</h2>
      <button id="b-perguntas">${icon('config')} Cadastrar e editar perguntas</button>
    </div>
    ${trilha(4)}
    <div class="cartao">
      ${(perguntas || []).length
        ? `<form id="f" class="linha-form">${perguntas.map(campo).join('')}</form>`
        : '<p class="vazio">Nenhuma pergunta cadastrada. Crie as suas no botão acima.</p>'}
    </div>
    <div class="rodape-passo">
      <button class="primaria" id="criar">${icon('contrato')} Criar o contrato</button>
    </div>`;

  raiz.querySelector('#voltar').onclick = () => passo3(st);
  raiz.querySelector('#b-perguntas').onclick = telaPerguntas;

  raiz.querySelector('#criar').onclick = async () => {
    const form = raiz.querySelector('#f');
    if (form) {
      if (!form.reportValidity()) return;
      st.respostas = Object.fromEntries(new FormData(form));
    }

    const btn = raiz.querySelector('#criar');
    btn.disabled = true;
    btn.textContent = 'Criando…';

    try {
      const { data: perfil } = await sb.from('perfis').select('tenant_id').limit(1).maybeSingle();
      const cond = { ...st.condicoes };
      delete cond.prazo;    // derivado das datas: prazo_meses é coluna gerada

      const c = checar(await sb.from('contratos').insert({
        tenant_id: perfil.tenant_id,
        inquilino_id: st.inquilino.id,
        ...cond,
        respostas: st.respostas,
        status: 'rascunho'
      }).select().single());

      // Daqui em diante o contrato tem ficha própria — que é a tela para
      // onde a pessoa volta amanhã, e não um passo que se vive uma vez só.
      fichaContrato(c.id);

    } catch (e) {
      btn.disabled = false;
      btn.textContent = 'Criar o contrato';
      const box = document.createElement('div');
      box.className = 'aviso erro';
      box.textContent = e.message;
      raiz.querySelector('.rodape-passo').prepend(box);
    }
  };
}
